import { Button, Col, Dropdown, Layout, Menu, Row, Space, Tag, Tooltip, Badge } from "antd";
import { useEffect, useState } from "react";
import { Link, Outlet, useNavigate, useParams } from "react-router-dom";
import { getNotificationService, getPipelinesService, getProjectService } from "../../backend";

import Logo from "../../assets/logo-color-circle.svg";
import DiscordLogo from "../../assets/discord-logo-white.svg";
import Avatar from "antd/lib/avatar/avatar";
import { Content, Footer } from "antd/lib/layout/layout";
import { useAuth } from "../../contexts/Auth";
import PageContentWrapper from "../../components/PageContentWrapper";
import { useRecoilState, useRecoilValue, useResetRecoilState } from "recoil";
import { currentProjectState, projectsState } from "../../recoil/project";
import {
    UserOutlined,
    PlusCircleOutlined,
    DownOutlined,
    YoutubeFilled,
    MediumOutlined,
    TwitterOutlined,
    CrownOutlined,
    CloudUploadOutlined,
    GiftFilled,
    BellFilled
} from "@ant-design/icons";
import { useCurrentProject } from "../../contexts/Project";
import { handleSetMixpanelUserGroups, handleTrackLogoutEvent } from "../../backend/tracking";
import { getExecutingTemplates, setExecutingTemplates } from "../../utils/local-storage";
import { ExecutingTemplate } from "../../types";
import { trackDeployAppClicked } from "../../backend/tracking/templates";
import ChangelogDrawer from "./ChangelogDrawer";
import { trackNotificationsClicked, trackWhatsNewClicked } from "../../backend/tracking/user-settings";
import NotificationsDrawer from "./NotificationsDrawer";

const FIFTEEN_MINUTES_IN_MILLISECONDS = 900000;

const PageLayout = () => {
    const badgeColor = "#1890ff";
    const { envId } = useParams();
    const navigate = useNavigate();
    const { isLoggedIn } = useAuth();
    const [loadingProjects, setLoadingProjects] = useState(false);
    const [projects, setProjects] = useRecoilState(projectsState)
    const currentProject = useRecoilValue(currentProjectState);
    const [numberOfExecutingTemplates, setNumberOfExecutingTemplates] = useState<number>(0);
    const resetProjectsState = useResetRecoilState(projectsState);
    const { updateCurrentProject, paymentMethodRequired, trialDaysLeft, isProjectActive } = useCurrentProject();
    const { profile } = useAuth();
    const [showChangelog, setShowChangelog] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [numberOfNotifications, setNumberOfNotifications] = useState(0);

    useEffect(() => {
        const loadData = async () => {
            setLoadingProjects(true);
            try {
                const { data: { projects } } = await getProjectService().listProjects();
                setProjects(projects);
                handleSetMixpanelUserGroups(projects.map(item => item.id));
            } catch (error) {
            } finally {
                setLoadingProjects(false);
            }
        }

        loadData();
    }, []);

    useEffect(() => {
        const loadExecutingTemplatesDetails = async () => {
            const executingTemplates = getExecutingTemplates();
            let templatesExtendedWithTime: ExecutingTemplate[] = [];
            templatesExtendedWithTime = await Promise.all(
                executingTemplates.map(async (t: ExecutingTemplate) => {
                    try {
                        if (!t.finished) {
                            const { data: templateExecution } = await getPipelinesService().getPipelineBuild(
                                currentProject!.id,
                                t.templateExecutionId!,
                                t.buildId
                            )
                            return {
                                ...t,
                                started: Number(templateExecution.startDate),
                                finished: Number(templateExecution.stopDate!),
                                status: templateExecution.status!
                            };
                        }
                        return t;
                    } catch (e) {
                        return {
                            ...t,
                            // set it as finished 15 mins ago so that it is removed from the list
                            finished: Date.now() - FIFTEEN_MINUTES_IN_MILLISECONDS
                        };
                    };
                })
            )
            // keep the templates that have finished less than 15 mins ago OR are still running
            const filtered = templatesExtendedWithTime
                .filter(
                    (temp: ExecutingTemplate) =>
                        (temp.finished && (temp.finished! + FIFTEEN_MINUTES_IN_MILLISECONDS) > new Date().getTime()) || !temp?.finished
                )

            setNumberOfExecutingTemplates(filtered.filter((t: ExecutingTemplate) => !t.finished).length);

            // remove from localStorage the ones that are no longer running
            setExecutingTemplates(filtered);
        }

        loadExecutingTemplatesDetails();

        const interval = setInterval(async () => {
            await loadExecutingTemplatesDetails();
        }, 10000);

        return () => clearInterval(interval);
    }, [])

    useEffect(() => {
        fetchNotifications();

        const interval = setInterval(async () => {
            await fetchNotifications();
        }, 30000);

        return () => clearInterval(interval);
    }, [])


    const handleSelectedProject = async (projectId: string) => {
        await updateCurrentProject(projectId);

        (window as Window).location = `/projects/${projectId}/environments`
    }

    const handleLogout = () => {
        localStorage.removeItem('idToken');
        localStorage.removeItem('expirationDate');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('profile');

        resetProjectsState();

        handleTrackLogoutEvent();

        navigate("/auth/login");
    }

    const handleOpenChangelog = () => {
        setShowChangelog(true);
        trackWhatsNewClicked();
    }

    const handleOpenNotifications = () => {
        setShowNotifications(true);
        trackNotificationsClicked();
    }

    const fetchNotifications = async () => {
        const { data: { notifications } } = await getNotificationService().getAllUserNotifications();
        setNumberOfNotifications(notifications.filter(notification => !notification.isRead).length);
    }

    return (
        <Layout style={{ minHeight: '100vh' }} >
            <Layout.Header className="header" style={{ position: 'fixed', zIndex: 1, width: '100%' }}>
                <PageContentWrapper>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <Space>
                            <Link to={`/projects/${currentProject?.id}`}>
                                <img src={Logo} height="35px" />
                            </Link>
                            {
                                loadingProjects || projects.length === 0 ? undefined :
                                    <>
                                        <Dropdown
                                            trigger={["click"]}
                                            overlay={
                                                <Menu>
                                                    {
                                                        projects.map(project => (
                                                            <Menu.Item key={project.id} onClick={() => handleSelectedProject(project.id)}>
                                                                {project.name}
                                                            </Menu.Item>
                                                        ))
                                                    }
                                                    <Menu.Divider />
                                                    <Menu.Item key="new-project">
                                                        <Link to="/pricing/new">
                                                            <PlusCircleOutlined /> Create Project
                                                        </Link>
                                                    </Menu.Item>
                                                </Menu>
                                            }>
                                            <Button type="ghost" className="ant-btn-ghost-inverse" style={{ float: 'right', border: "none", boxShadow: "none", fontWeight: 600 }}>
                                                {currentProject?.name} &nbsp;<Tag className="ant-btn-ghost-inverse">{currentProject?.paymentPlan?.name}</Tag> <DownOutlined />
                                            </Button>
                                        </Dropdown>
                                    </>
                            }
                        </Space>
                        <Space>
                            {
                                isLoggedIn ?
                                    <>
                                        {/* Hide this content on mobile for now, maybe display it differently */}
                                        <div className="header-hide-mobile">
                                            <Space>
                                                {
                                                    isProjectActive &&
                                                    paymentMethodRequired &&
                                                    trialDaysLeft && trialDaysLeft > 0 &&
                                                    <span className="info-box">
                                                        <Space>
                                                            <CrownOutlined />
                                                            <Tooltip title={`You still don't have a payment method added. Add a payment method to be able to use the project after the trial ends.`}>
                                                                Your Free Trial ends in {trialDaysLeft} days.
                                                            </Tooltip>
                                                        </Space>
                                                        <Link to={`projects/${currentProject.id}/settings/billing`}> Update Billing</Link>
                                                    </span>
                                                }
                                                {
                                                    (!!currentProject && !isProjectActive) ?
                                                        <span className="info-box">
                                                            <Space>
                                                                <CrownOutlined />
                                                                <Tooltip title="Your project is currently in an inactive state. Upgrade the project plan to continue using the project.">
                                                                    Your Free Trial ended.
                                                                </Tooltip>
                                                            </Space>
                                                            <Link to="pricing/activate"> Upgrade now</Link>
                                                        </span>
                                                        : null
                                                }
                                                {
                                                    <div style={{ paddingLeft: 8, paddingRight: 8 }}>
                                                        <Badge style={{ backgroundColor: badgeColor }} count={numberOfExecutingTemplates} size="small" title='Recently executed templates'>
                                                            <Link to="/templates" state={{ envId }}>
                                                                <Button type="ghost" className="ant-btn-ghost-inverse" onClick={() => trackDeployAppClicked()}>
                                                                    <CloudUploadOutlined /> Deploy app
                                                                </Button>
                                                            </Link>
                                                        </Badge>
                                                    </div>
                                                }

                                                <Button
                                                    type="text"
                                                    icon={<GiftFilled />}
                                                    className="ant-btn-ghost-inverse"
                                                    onClick={handleOpenChangelog}
                                                />

                                                <div style={{ marginRight: 4 }}>
                                                    <Badge
                                                        style={{ backgroundColor: badgeColor }}
                                                        count={numberOfNotifications}
                                                        size="small"
                                                        offset={[-2, 0]}>
                                                        <Button
                                                            type="text"
                                                            icon={<BellFilled />}
                                                            className="ant-btn-ghost-inverse"
                                                            onClick={handleOpenNotifications}
                                                        />
                                                    </Badge>
                                                </div>
                                            </Space>
                                        </div>
                                        <Dropdown
                                            placement="bottomRight"
                                            overlay={
                                                <Menu>
                                                    <Menu.Item key="profile-email" disabled>
                                                        {profile?.email}
                                                    </Menu.Item>
                                                    <Menu.Item key="logout" onClick={handleLogout}>
                                                        Logout
                                                    </Menu.Item>
                                                </Menu>
                                            }
                                            trigger={['click']}
                                        >
                                            <a className="ant-dropdown-link" onClick={e => e.preventDefault()}>
                                                <Avatar icon={<UserOutlined />} />
                                            </a>
                                        </Dropdown>
                                    </> :
                                    <>
                                        <Button type="link" className="ant-btn-ghost-inverse">
                                            <Link to="/auth/login">
                                                Login
                                            </Link>
                                        </Button>
                                        <Button type="ghost" className="ant-btn-ghost-inverse">
                                            <Link to="/auth/signup">
                                                Sign Up
                                            </Link>
                                        </Button>
                                    </>
                            }
                        </Space>
                    </div>
                </PageContentWrapper>
            </Layout.Header>
            <Content style={{ marginTop: 64 }}>
                <Outlet />
            </Content>
            <div className="footer">
                <Footer>
                    <Row gutter={24}>
                        <Col md={8} xs={24}>
                            <span>
                                © {new Date().getFullYear()} Microtica Inc. All rights reserved.
                            </span>
                        </Col>
                        <Col md={8} xs={24}>
                            <Space size="large">
                                <a href="https://docs.microtica.com/" target="_blank">Docs</a>
                                <a href="https://microtica.com/blog/" target="_blank" >Blog</a>
                                <a href="https://microtica.com/pricing/" target="_blank">Pricing</a>
                                <a href="https://microtica.com/terms-of-service/" target="_blank">Terms</a>
                            </Space>
                        </Col>
                        <Col md={8} xs={24}>
                            <Space size="middle">
                                <a href="https://www.youtube.com/channel/UC1ygyD_CjW8NBBvwKI-qK6g" target="_blank">
                                    <YoutubeFilled style={{ fontSize: 17 }} />
                                </a>
                                <a href="https://discord.gg/ADaFvAsakW" target="_blank">
                                    <img style={{ width: 17, marginTop: -5 }} src={DiscordLogo} />
                                </a>
                                <a href="https://medium.com/microtica" target="_blank">
                                    <MediumOutlined style={{ fontSize: 17 }} />
                                </a>
                                <a href="https://twitter.com/microtica" target="_blank">
                                    <TwitterOutlined style={{ fontSize: 17 }} />
                                </a>
                            </Space>
                        </Col>
                    </Row>
                </Footer>
            </div>
            <ChangelogDrawer visible={showChangelog} onClose={() => setShowChangelog(false)} />
            {
                showNotifications &&
                <NotificationsDrawer
                    projects={projects}
                    visible={showNotifications}
                    onClose={() => {
                        setShowNotifications(false);
                        fetchNotifications();
                    }}
                />
            }
        </Layout>
    );
}

export default PageLayout;